import Button from '@ingka/button';
import Text from '@ingka/text';
import { useState } from 'react';
import {
  type CatalogEntityAdmin,
  type CatalogProductAdmin,
  createAdminEntity,
  updateAdminEntity,
} from '@/api/client';
import CatalogItemModal from '@/components/admin/CatalogItemModal';
import styles from './AdminCatalog.module.css';

type EntityModalState =
  | { open: false }
  | { open: true; mode: 'create' }
  | { open: true; mode: 'edit'; entity: CatalogEntityAdmin };

interface Props {
  product: CatalogProductAdmin;
  onReload: () => Promise<void>;
}

export default function AdminEntityList({ product, onReload }: Props) {
  const [modal, setModal] = useState<EntityModalState>({ open: false });

  const handleSave = async (data: {
    id: string;
    name: string;
    description: string;
    active?: boolean;
  }) => {
    if (modal.open && modal.mode === 'create') {
      await createAdminEntity(product.id, {
        id: data.id,
        name: data.name,
        description: data.description || null,
      });
    } else if (modal.open && modal.mode === 'edit') {
      await updateAdminEntity(product.id, modal.entity.id, {
        name: data.name,
        description: data.description || null,
        active: data.active,
      });
    }
    setModal({ open: false });
    await onReload();
  };

  return (
    <div className={styles.entityListWrap}>
      <div className={styles.entityListHeader}>
        <Text tagName="h3" textStyle="Heading.S">
          Entities in {product.name}
        </Text>
        <Button
          type="secondary"
          size="small"
          text="Add entity"
          onClick={() => setModal({ open: true, mode: 'create' })}
        />
      </div>

      {product.entities.length === 0 ? (
        <Text tagName="p" textStyle="Body.M" className={styles.emptyText}>
          No entities yet. Add the first one.
        </Text>
      ) : (
        <table className={styles.subTable}>
          <thead>
            <tr>
              <th>Name</th>
              <th>ID</th>
              <th>Description</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {product.entities.map((e) => (
              <tr key={e.id}>
                <td>{e.name}</td>
                <td>
                  <code className={styles.idCell}>{e.id}</code>
                </td>
                <td>{e.description || '—'}</td>
                <td>
                  {e.active ? (
                    <span className={styles.activeOn}>●</span>
                  ) : (
                    <span className={styles.activeOff}>●</span>
                  )}
                </td>
                <td>
                  <Button
                    type="secondary"
                    size="small"
                    text="Edit"
                    onClick={() => setModal({ open: true, mode: 'edit', entity: e })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal.open && (
        <CatalogItemModal
          title={modal.mode === 'create' ? `Add entity to ${product.name}` : 'Edit entity'}
          mode={modal.mode}
          initial={
            modal.mode === 'edit'
              ? {
                  id: modal.entity.id,
                  name: modal.entity.name,
                  description: modal.entity.description ?? '',
                  active: modal.entity.active,
                }
              : undefined
          }
          warningBody="Ensure the data team has updated Databricks Autoloader to handle this entity."
          onClose={() => setModal({ open: false })}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
